import type { RpaBootstrap } from '../api'
import { getContactDetail } from '../api/contacts'
import useMessagesStore, { type IncomingMessage } from '../store/messages'
import useThreadsStore from '../store/threads'
import useContactsListStore from '../store/contactsList'
import useContactsDetailStore from '../store/contactsDetail'
import useShowClockLiveStore from '../store/showClockLive'
import type { ClockLiveActionResponse } from '../api/showClockLive'

interface ConnectParams {
  stationId: number
  unreadOnly?: boolean
  lastId?: number
  lastUpdateId?: number
  onEvent?: (payload: unknown, eventType: string) => void
  onOpen?: () => void
  onError?: (event: Event) => void
}

export interface SSEController {
  close: () => void
}

const getBootstrap = (): RpaBootstrap => {
  const bootstrap = window.__RPA_BOOTSTRAP__

  if (!bootstrap?.restBase || !bootstrap?.nonce) {
    throw new Error('Missing REST configuration for SSE connection.')
  }

  return bootstrap
}

export function connectSSE({
  stationId,
  unreadOnly = false,
  lastId = 0,
  lastUpdateId = 0,
  onEvent,
  onOpen,
  onError,
}: ConnectParams): SSEController {
  let source: EventSource | null = null
  let closed = false
  let reconnectDelay = 500
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let latestId = lastId
  let latestUpdateId = lastUpdateId
  let hasLoggedInitialOpen = false

  const { restBase, nonce } = getBootstrap()
  const isDev = Boolean((import.meta as any)?.env?.DEV)

  const buildUrl = () => {
    const url = new URL('sse', restBase)
    url.searchParams.set('station_id', String(stationId))
    url.searchParams.set('last_id', String(latestId))
    url.searchParams.set('last_update_id', String(latestUpdateId))
    url.searchParams.set('unread', unreadOnly ? '1' : '0')
    url.searchParams.set('_wpnonce', nonce)
    const lastContactEventId = useContactsListStore.getState().lastEventId
    if (lastContactEventId > 0) {
      url.searchParams.set('last_contact_event_id', String(lastContactEventId))
    }
    return url.toString()
  }

  const cleanup = () => {
    if (source) {
      source.close()
      source = null
    }
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
  }

  const scheduleReconnect = () => {
    if (closed) {
      return
    }

    cleanup()

    const delay = reconnectDelay
    reconnectDelay = Math.min(reconnectDelay * 2, 10000)

    reconnectTimer = setTimeout(() => {
      if (!closed) {
        connect()
      }
    }, delay)
  }

  const isIncomingMessage = (payload: unknown): payload is IncomingMessage => {
    if (!payload || typeof payload !== 'object') {
      return false
    }

    const candidate = payload as { id?: unknown; contact_identity_id?: unknown; contact?: unknown }
    return (
      typeof candidate.id === 'number' &&
      typeof candidate.contact_identity_id === 'number' &&
      candidate.contact !== undefined
    )
  }

  const handleMessage = (event: MessageEvent<string>, eventType: string) => {
    if (!event.data) {
      return
    }

    try {
      const payload = JSON.parse(event.data)

      if (eventType === 'message' && payload && typeof payload === 'object' && 'id' in payload && typeof payload.id === 'number') {
        latestId = Math.max(latestId, payload.id)
        if (isIncomingMessage(payload)) {
          useMessagesStore.getState().appendBatch([payload])
          useThreadsStore.getState().upsertFromMessage(payload)
        }
      }

      if (eventType === 'message:update' && payload && typeof payload === 'object') {
        const update = payload as {
          update_id?: unknown
          message_id?: unknown
          transport_status?: unknown
          transport_error?: unknown
        }

        const messageId = Number(update.message_id)

        if (!Number.isNaN(messageId) && messageId > 0) {
          const updates: Partial<IncomingMessage> = {}

          if (typeof update.transport_status === 'string') {
            updates.transport_status = update.transport_status as IncomingMessage['transport_status']
          }

          if (typeof update.transport_error === 'string' && update.transport_error.trim().length > 0) {
            updates.transport_error = update.transport_error
          } else {
            updates.transport_error = null
          }

          if (Object.keys(updates).length > 0) {
            useMessagesStore.getState().updateMessage(messageId, updates)
          }
        }

        const updateId = Number(update.update_id)

        if (!Number.isNaN(updateId) && updateId > 0) {
          latestUpdateId = Math.max(latestUpdateId, updateId)
          useMessagesStore.getState().setLastUpdateId(latestUpdateId)
        }
      }

      onEvent?.(payload, eventType)
    } catch (error) {
      console.error('Failed to parse SSE payload', error)
    }
  }

  const handleClockEvent = (event: MessageEvent<string>, eventType: string) => {
    if (!event.data) {
      return
    }

    try {
      const payload = JSON.parse(event.data) as ClockLiveActionResponse
      if (payload && typeof payload === 'object') {
        useShowClockLiveStore.getState().applyActionPayload(payload)
      }
      onEvent?.(payload, eventType)
    } catch (error) {
      console.error('Failed to process clock live event', error)
    }
  }

  const handleContactEvent = async (event: MessageEvent<string>, eventType: string, currentStationId: number) => {
    if (!event.data) {
      return
    }

    try {
      const payload = JSON.parse(event.data) as Record<string, unknown>

      switch (eventType) {
        case 'contact.updated': {
          const contactId = Number(payload.contact_id)
          if (Number.isNaN(contactId) || contactId <= 0) {
            return
          }
          await refreshContactDetail(contactId, currentStationId)
          break
        }
        case 'message.on_air.marked':
        case 'message.on_air.cleared':
        case 'message.flag.marked':
        case 'message.flag.cleared': {
          const threadId = Number((payload.thread_id ?? payload.threadId) as number)
          const messageId = Number((payload.message_id ?? payload.messageId) as number)
          const messagePayload = payload.message as Record<string, unknown> | undefined

          if (!Number.isNaN(messageId) && messageId > 0) {
            const updates: Partial<IncomingMessage> = {}
            if (messagePayload && typeof messagePayload === 'object') {
              if ('is_on_air' in messagePayload) {
                updates.is_on_air = Boolean(messagePayload.is_on_air)
              }
              if ('is_flagged' in messagePayload) {
                updates.is_flagged = Boolean(messagePayload.is_flagged)
              }
            }
            useMessagesStore.getState().updateMessage(messageId, updates)
          }

          if (!Number.isNaN(threadId) && threadId > 0) {
            const threadSummary = payload.thread as Record<string, unknown> | undefined
            if (threadSummary) {
              useThreadsStore.getState().applyThreadStatus(threadId, threadSummary)
            }
          }
          break
        }
        case 'thread.on_air.cleared':
        case 'thread.flag.cleared': {
          const threadId = Number((payload.thread_id ?? payload.threadId) as number)
          const messageIds = Array.isArray(payload.message_ids) ? payload.message_ids : []
          const updates: Partial<IncomingMessage> =
            eventType === 'thread.on_air.cleared'
              ? { is_on_air: false }
              : { is_flagged: false }

          messageIds.forEach((value) => {
            const id = Number(value)
            if (!Number.isNaN(id) && id > 0) {
              useMessagesStore.getState().updateMessage(id, updates)
            }
          })

          if (!Number.isNaN(threadId) && threadId > 0) {
            const threadSummary = payload.thread as Record<string, unknown> | undefined
            if (threadSummary) {
              useThreadsStore.getState().applyThreadStatus(threadId, threadSummary)
            }
          }

          break
        }
        case 'contact.blocked': {
          const contactId = Number(payload.contact_id)
          const blocked = Boolean(payload.blocked)
          if (Number.isNaN(contactId) || contactId <= 0) {
            return
          }
          useContactsListStore.getState().setBlockedState(contactId, blocked)
          const entry = useContactsDetailStore.getState().byId[contactId]
          if (entry?.data) {
            useContactsDetailStore.getState().setContact(contactId, {
              ...entry.data,
              contact: { ...entry.data.contact, blocked },
            })
          }
          break
        }
        case 'contact.merged': {
          const fromId = Number(payload.from_contact_id)
          const toId = Number(payload.to_contact_id)
          if (Number.isNaN(fromId) || Number.isNaN(toId) || fromId <= 0 || toId <= 0) {
            return
          }
          useContactsDetailStore.getState().invalidate(fromId)
          useContactsListStore.getState().removeContact(fromId)
          useContactsDetailStore.getState().setLastMergeEvent({ fromContactId: fromId, toContactId: toId })
          await refreshContactDetail(toId, currentStationId)
          break
        }
        case 'identity.blocked': {
          const identityId = Number(payload.identity_id)
          const blocked = Boolean(payload.blocked)
          if (Number.isNaN(identityId) || identityId <= 0) {
            return
          }
          const detailStore = useContactsDetailStore.getState()
          Object.entries(detailStore.byId).forEach(([key, entry]) => {
            if (!entry?.data) {
              return
            }
            const identityIndex = entry.data.identities.findIndex((identity) => identity.id === identityId)
            if (identityIndex === -1) {
              return
            }
            const nextIdentities = [...entry.data.identities]
            nextIdentities[identityIndex] = { ...nextIdentities[identityIndex], blocked }
            const nextDetail = { ...entry.data, identities: nextIdentities }
            const contactId = Number(key)
            useContactsDetailStore.getState().setContact(contactId, nextDetail)
            useContactsListStore.getState().upsertFromDetail(nextDetail)
          })
          break
        }
        case 'identity.reassigned': {
          const identityId = Number(payload.identity_id)
          const fromId = Number(payload.from_contact_id)
          const toId = Number(payload.to_contact_id)
          if (Number.isNaN(identityId) || Number.isNaN(fromId) || Number.isNaN(toId)) {
            return
          }
          useContactsDetailStore.getState().setLastIdentityReassignedEvent({
            identityId,
            fromContactId: fromId,
            toContactId: toId,
          })
          if (fromId > 0) {
            await refreshContactDetail(fromId, currentStationId)
          }
          if (toId > 0) {
            await refreshContactDetail(toId, currentStationId)
          }
          break
        }
        default:
          break
      }
    } catch (error) {
      console.error('Failed to process contact event', error)
    }
  }

  const connect = () => {
    cleanup()

    if (closed) {
      return
    }

    try {
      source = new EventSource(buildUrl(), { withCredentials: true })
    } catch (error) {
      console.error('Unable to start SSE connection', error)
      const evt = error instanceof Event ? error : new Event('error')
      onError?.(evt)
      scheduleReconnect()
      return
    }

    source.onopen = () => {
      reconnectDelay = 500
      if (isDev && !hasLoggedInitialOpen) {
        console.log('[RPA] SSE connected', {
          stationId,
          unreadOnly,
        })
        hasLoggedInitialOpen = true
      }
      onOpen?.()
    }

    source.addEventListener('message', (event) => handleMessage(event as MessageEvent<string>, 'message'))
    source.addEventListener('stats', (event) => handleMessage(event as MessageEvent<string>, 'stats'))
    source.addEventListener('message:update', (event) => handleMessage(event as MessageEvent<string>, 'message:update'))
    const handleContact = (event: Event, type: string) => {
      void handleContactEvent(event as MessageEvent<string>, type, stationId)
    }
    source.addEventListener('contact.updated', (event) => handleContact(event, 'contact.updated'))
    source.addEventListener('contact.blocked', (event) => handleContact(event, 'contact.blocked'))
    source.addEventListener('contact.merged', (event) => handleContact(event, 'contact.merged'))
    source.addEventListener('identity.blocked', (event) => handleContact(event, 'identity.blocked'))
    source.addEventListener('identity.reassigned', (event) => handleContact(event, 'identity.reassigned'))
    source.addEventListener('clock.item.started', (event) =>
      handleClockEvent(event as MessageEvent<string>, 'clock.item.started'),
    )
    source.addEventListener('clock.item.ended', (event) =>
      handleClockEvent(event as MessageEvent<string>, 'clock.item.ended'),
    )
    source.addEventListener('clock.item.skipped', (event) =>
      handleClockEvent(event as MessageEvent<string>, 'clock.item.skipped'),
    )
    source.addEventListener('clock.item.adjusted', (event) =>
      handleClockEvent(event as MessageEvent<string>, 'clock.item.adjusted'),
    )

    source.addEventListener('error', (event) => {
      if (isDev) {
        console.error('[RPA] SSE stream error', event)
      }
      onError?.(event)

      if (closed) {
        return
      }

      const readyState = (source as EventSource | null)?.readyState

      if (!readyState || readyState === EventSource.CLOSED) {
        scheduleReconnect()
      } else {
        scheduleReconnect()
      }
    })
  }

  connect()

  return {
    close: () => {
      closed = true
      cleanup()
    },
  }
}

const refreshContactDetail = async (contactId: number, stationId?: number | null) => {
  const existing = useContactsDetailStore.getState().byId[contactId]
  const activeStationId = useMessagesStore.getState().activeStationId ?? null
  const resolvedStationId =
    (stationId && stationId > 0 ? stationId : null) ??
    (existing?.stationId && existing.stationId > 0 ? existing.stationId : null) ??
    (activeStationId && activeStationId > 0 ? activeStationId : null)

  if (!resolvedStationId) {
    return
  }

  try {
    const detail = await getContactDetail(resolvedStationId, contactId)
    useContactsDetailStore.getState().setContact(contactId, detail, resolvedStationId)
    useContactsListStore.getState().upsertFromDetail(detail)
  } catch (error) {
    if ((import.meta as any)?.env?.DEV) {
      console.warn('Unable to refresh contact detail after event', error)
    }
  }
}
